import React from 'react';

import {
  FormConfig,
  FormContextType,
  FormLayout,
  FormStructure,
  FormValuesConstraint,
  StepNode,
} from '../types';
import {getStepAtPath} from '../utils/structure';

import {FormContext} from './FormContext';
import useForm from './useForm';

interface LayoutRendererProps<
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
  K,
> {
  render: FormLayout<T, S, K>;
  step: React.ReactNode;
  ctx: FormContextType<T, S, K>;
}

/*
 * The layout is a render prop, and layouts in this codebase call hooks -
 * `useState`, store hooks, whatever the header needs. Invoking it inline from
 * FormProvider made those hooks part of FormProvider's own hook list, sitting
 * after a step lookup that can throw. When the lookup threw, the layout never
 * ran, FormProvider rendered fewer hooks than the render before it, and React
 * reported "Rendered fewer hooks than expected" instead of the actual error.
 *
 * Rendering the layout as a component gives it its own hook list. A throw in
 * the step lookup now unmounts this cleanly and surfaces the real error at the
 * nearest boundary.
 */
const LayoutRenderer = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
  K,
>({
  render,
  step,
  ctx,
}: LayoutRendererProps<T, S, K>) => <>{render(step, ctx)}</>;

export const FormProvider = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
  K = undefined,
>(
  props: FormConfig<T, S, K>,
) => {
  const ctx = useForm<T, S, K>(props);

  const {structure, children} = props;
  const {navigation} = ctx;

  const renderStep = () => {
    try {
      const currentHistory = navigation.history[navigation.history.length - 1];
      /*
       * Asserted rather than guarded on purpose. A bad path must surface as the
       * `catch` below turning it into the "Invalid Step structure" error, which
       * is what a plain `step.component` on `undefined` does. The two obvious
       * alternatives both make React Compiler bail out of this whole component
       * and drop its memo cache — verified by compiling the file: `step?.component`
       * and an `if (!step) throw` each emit zero memoization guards, while this
       * emits four.
       */
      const step = getStepAtPath<T>(
        structure,
        currentHistory.path,
      ) as StepNode<T>;
      const Component = step.component;

      return <Component />;
    } catch (error) {
      throw new Error(
        `Invalid Step structure: ${error} \n
        Form History: ${JSON.stringify(navigation.history)} \n
      `,
        {cause: error},
      );
    }
  };

  const step = renderStep();

  return (
    <FormContext.Provider value={ctx}>
      {typeof children === 'function' ? (
        <LayoutRenderer<T, S, K> render={children} step={step} ctx={ctx} />
      ) : (
        step
      )}
    </FormContext.Provider>
  );
};
